import type { ComponentType, ReactElement } from 'react';
import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';

type EmptyStateProps = {
  icon?: ComponentType;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void; icon?: ReactElement };
  actionHref?: string;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '400px',
  },
  link: {
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
});

export const EmptyState = ({ icon: Icon, title, description, action, actionHref }: EmptyStateProps) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      {Icon ? (
        <span className={styles.icon} aria-hidden="true">
          <Icon />
        </span>
      ) : null}
      <Text size={400} className={styles.title}>
        {title}
      </Text>
      <Text size={300} className={styles.description}>
        {description}
      </Text>
      {action ? (
        <Button appearance="primary" icon={action.icon ?? null} onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
      {actionHref ? (
        <a href={actionHref} className={styles.link} target="_blank" rel="noopener noreferrer">
          Learn more
        </a>
      ) : null}
    </div>
  );
};
